/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useState, useEffect, useRef, forwardRef, useMemo } from 'react'
import { Header, Table, Segment, Icon, Accordion, Label, Button, Divider, Popup, Checkbox } from 'semantic-ui-react'
import { UltyMoney, UltyInputText as UltyInput } from '@ulty/ulty-ui'
import { sortAsc } from '../../../helpers/array'
import styled from '@emotion/styled'
import { useQuery } from '@tanstack/react-query'
import { getItems } from '../../../services/Margin/margin.service'
import { TableVirtuoso  } from 'react-virtuoso'
import { unitFormat } from '../../../helpers/money'
import useUser from '../../Shared/UserProvider/useUser'
import { parse, parseAsync } from 'json2csv'
import { saveFile } from '../../../services/File/File.service'
import { useTranslation } from 'react-i18next'
import useLocalStorage from 'beautiful-react-hooks/useLocalStorage'


const useProducts = () => {

  const query = useQuery({
    queryKey: ['margin/products'],
    queryFn: getItems,
    staleTime: 1000 * 60 * 5,
  })

  return query;

}

const FlexedDiv = styled.div`
display:flex;
justify-content: space-between
`;

const StrongFlexDiv = styled.div`
display:flex;
justify-content: space-between;
font-weight: 600;
`;

const AmountLabel = ({amount,label,currencyCode, offset=0, strong=false, info=""}) => {
  const Contain = strong ? StrongFlexDiv : FlexedDiv;
  const Label = <label css={css`padding-left:${offset*2}em`}>{label} {info && <Icon name="info circle"/>}</label>;

  const LabelWithInfoWhenNeeded = info ? <Popup content={<span css={css`white-space:nowrap`}>{info}</span>} trigger={Label} /> : Label;
  return <Contain>
    <>
      {LabelWithInfoWhenNeeded}
      <UltyMoney amount={amount || 0} currencyCode={currencyCode} inline readonly/>
    </>
  </Contain>
}

const fullLineHorizontalDividerStyle = css`
:before {
    width: 100%!important;
}
`

const CustomDivider = () => {
  return <Divider horizontal css={fullLineHorizontalDividerStyle}><span>&nbsp;</span></Divider>
}

const DisplayMarginLabel = ({product,margin, offset=0,basic=false}) => {
  return  <FlexedDiv>
  {(margin === null || isNaN(margin)) ? <span> - </span> : <span>{percentFormatter.format(margin ?? 0)}%</span>}
</FlexedDiv>
}

const DetailStorePriceDisplay = ({product,currencyCode, marginType}) => {
  const inStorePrice = product.sellPrice || product.item.basePrice || 0;
  const priceInfo = computeValuesFromPrice(product,inStorePrice,null,  marginType);

  return <DetailPriceDisplay hidePlatformInfo priceInfo={priceInfo} currencyCode={currencyCode} product={product}/>
}
const DetailPlatformPriceDisplay = ({product,platform,currencyCode, marginType}) => {


  const priceInfo = computeValues(product,platform, marginType)

  return <DetailPriceDisplay hidePCE priceInfo={priceInfo} currencyCode={currencyCode} product={product}/>

}

const DetailPriceDisplay = ({
    product,
    priceInfo,
    currencyCode,
    hidePlatformInfo=false,
    hidePCE=false
}) => {
  const {
    costHt,
    sellPriceExclVat,
    commissionVatAmount,
    estimatedRevenue,
    margin,
    sellPriceExclVatCommissionExcl,
    estimatedPlatformPayout,
    commissionAmount, 
    vatAmount,
    appliedSellPrice,
    platformCommissionRate,
  } = priceInfo;

  return <>
  <CustomDivider/>
  {hidePCE ? (<br/>) : <strong><UltyMoney readonly inline amount={costHt || 0} currencyCode={currencyCode} /></strong>}

  <UltyMoney readonly amount={appliedSellPrice || 0} currencyCode={currencyCode}/>
  <UltyMoney readonly amount={vatAmount || 0} currencyCode={currencyCode} />
  <strong><UltyMoney readonly amount={sellPriceExclVat || 0} currencyCode={currencyCode} /></strong>
  <CustomDivider/>
  {hidePlatformInfo ? (<>
    <br/>
    <br/>
    <br/>
    <br/>
  </>) : (<>
    <div>{percentFormatter.format(platformCommissionRate ?? 0)}%</div>
    <strong><UltyMoney readonly amount={commissionAmount || 0} currencyCode={currencyCode} inline /></strong>
    <UltyMoney readonly amount={commissionVatAmount || 0} currencyCode={currencyCode} />
    <UltyMoney strong readonly amount={estimatedPlatformPayout|| 0} currencyCode={currencyCode}/>
  </>)}
    <CustomDivider/>
 
  <strong><UltyMoney  strong readonly amount={estimatedRevenue || 0} currencyCode={currencyCode} /></strong>
  <strong><DisplayMarginLabel strong product={product} margin={margin} /></strong>
  </>
}

const shortBorderLeftDividerStyle = css`
:after {
    width: 80%!important;
}
:before {
    width: unset!important;
  }
`;

const RightAlignedContentDiv = styled.div`
div { text-align:right; display:block;}
`

const percentFormatter = new Intl.NumberFormat('en-US', {
  useGrouping: false,       // "1 000" ➜ "1000"
  minimumFractionDigits: 0, // don’t force a decimal part
  maximumFractionDigits: 2  // …but show up to 2 if they matter
});


const DetailProductDisplay = ({product,currencyCode, marginType}) => {

  const {t} = useTranslation();

  return <div css={css`
    padding-left: 20px;
`}>
    <Divider css={shortBorderLeftDividerStyle} horizontal>{t('margin.details.prices.title')}</Divider>
    <RightAlignedContentDiv>
    <div><strong>{t('margin.details.prices.costExclVat',"PCE HT")}</strong></div>
    <div>{t('margin.details.prices.sellPriceIncVat',"Prix Vente TTC")}</div>
    <div>{t('margin.details.prices.vatAmount','TVA ({{vatPercent}}%)',{vatPercent:product?.item?.vat ? percentFormatter.format(product?.item?.vat) : 'N/A'})}</div>
    <div><strong>{t('margin.details.sellPriceExclVat',"Prix de vente HT")}</strong></div>
    </RightAlignedContentDiv>
   <Divider css={shortBorderLeftDividerStyle} horizontal>{t('margin.details.commission.title')}</Divider>
   <RightAlignedContentDiv>
    <div>{t("margin.details.commission.commissionRate")}</div>
    <div><strong>{t("margin.details.commission.commissionAmountExclVat")}</strong></div>
    <div>{t("margin.details.commission.commissionAmountVat")}</div>
    <div>{t("margin.details.commission.estimatedPayoutIncVat")}</div>
    </RightAlignedContentDiv>
   <Divider css={shortBorderLeftDividerStyle} horizontal>{t('margin.details.profit.title')}Rentabilité estimée</Divider>
   <RightAlignedContentDiv>
   <div><strong>{t('margin.details.profit.estimatedRevenueExclTax')}</strong></div>
   <div><strong>{t(`margin.details.profit.estimatedMargin_${marginType}`)}</strong></div>
   </RightAlignedContentDiv>
  </div>
}

const marginFilterOptions = [
  {value: 'FAIR', filterFunc : (margin,product) => product.costExclVat && margin > 30},
  {value: 'RISK', filterFunc : (margin,product) => product.costExclVat && margin > 0 && margin <= 30},
  {value: 'NEGATIVE', filterFunc : (margin,product) => product.costExclVat && margin <= 0},
  {value: 'NONE', filterFunc : (margin,product) => product.costExclVat === null},
]

const VirtuosoSemanticOverrides = {
 
  Table: forwardRef((props, ref) => <Table {...props} ref={ref} unstackable />),
  TableHead: (props) => <Table.Header {...props} style={{...props.style, top:60}}/>,
  TableBody: forwardRef((props, ref) => <Table.Body {...props} ref={ref} />),
  TableRow: (props) => (
    <Table.Row  {...props} style={{ display: "table-row" }} />
  )
}


const computeMargin = (product, platform, marginType) => {
  const isPlatformArray = Array.isArray(platform);
  if(isPlatformArray) {
    return platform.map(p => computeMargin(product,p,marginType)).reduce((a,b) => Math.min(a,b), 1000)
  }
  const priceInclVat = product.prices.find(p => p.platform.name === platform?.name)?.sellPrice;
  return computeMarginFromPrice(product,priceInclVat,platform,marginType);
};

const computeMarginFromPrice = (product,price,platform = null,marginType="ON_COST") => {
  const platformCommission = (platform?.commissionRate ?? 0)*0.01;
  const platfomCommissionAmount = platformCommission * price;
  const costExclVat = product.costExclVat
  if(!costExclVat) return null;
  const priceExclVat = price / (1 + product.item.vat / 100);
  const priceExclVatExclPlatformCommision = priceExclVat - platfomCommissionAmount;
  return marginType === "ON_SELLING_PRICE"
            ? ((priceExclVatExclPlatformCommision - costExclVat) / priceExclVat) * 100
            : ((priceExclVatExclPlatformCommision - costExclVat) / costExclVat) * 100
};

const computeValues = (product,platform,marginType) => {
  const appliedSellPrice = product?.prices.find(p => p.platform.name === platform.name)?.sellPrice ?? product.basePrice;

  return computeValuesFromPrice(product,appliedSellPrice,platform,marginType)
}

const computeValuesFromPrice = (product,sellPrice,platform=null,marginType) => {
  const platformCommissionRate = (platform?.commissionRate ?? 0)
  const costHt = Math.round(product.costExclVat);
  const appliedSellPrice = sellPrice;


  const commissionAmount = Math.round(appliedSellPrice * (platformCommissionRate) *0.01);
  const vatAmount = Math.round(appliedSellPrice * (product.item.vat * 0.01));

  const commissionVatAmount = Math.round(commissionAmount * 0.2);

  const sellPriceExclVat = appliedSellPrice - vatAmount
  const sellPriceExclVatCommissionExcl = sellPriceExclVat - commissionAmount

  const estimatedPlatformPayout = sellPrice - commissionAmount - commissionVatAmount;

  const estimatedRevenue = sellPriceExclVatCommissionExcl - costHt
  const margin = computeMarginFromPrice(product,sellPrice,platform,marginType)
  return {
    platformCommissionRate,
    costHt,
    sellPriceExclVat,
    sellPriceExclVatCommissionExcl,
    estimatedPlatformPayout,
    commissionAmount,
    commissionVatAmount,
    vatAmount,
    appliedSellPrice,
    estimatedRevenue,
    margin
  }
}


const computeColor = (product,margin) => {
  const value = marginFilterOptions.find(f => f.filterFunc(margin,product))?.value;
  if(!value) return;
  switch(value) {
    case 'FAIR':
      return 'teal';
    case 'RISK':
      return 'orange';
    case 'NEGATIVE':
      return 'red';
    case 'NONE':
      return 'grey';
    default:
      return
  }
}


const MarginProductTable = ({platformsCommissions}) => {
  const [t] = useTranslation()

  const {data:products, isLoading:productsLoading} = useProducts();
  const [displayDetails,setDisplayDetail] = useState(new Set());
  
  const [marginFilter, setMarginFilter] = useState('FAIR');
  const [itemSearch, setItemSearch] = useState('');

  const [marginType,setMarginType] = useLocalStorage('ulty.margin.type',"ON_SELLING_PRICE");

  const { user } = useUser();


  const productsWithMargin = useMemo(() => !products ? [] :  sortAsc(products.map(p => ({
    ...p,
    id:p.itemId,
    margin: computeMargin(p,platformsCommissions,marginType)
  })),p => p.margin), [products,platformsCommissions,marginType])

  const marginFilterDefinition = useMemo(
    () => marginFilterOptions.find(f => f.value === marginFilter),
    [marginFilter]
  );

  const displayedProduct = useMemo(() => {
    const normalizedQuery = itemSearch.trim().toLowerCase();

    return productsWithMargin
      .filter(p => !marginFilterDefinition || marginFilterDefinition.filterFunc(p.margin, p))
      .filter(p => {
        if (!normalizedQuery) {
          return true;
        }

        const name = p?.item?.name?.toLowerCase() ?? '';
        const barcode = p?.item?.barcode?.toLowerCase() ?? '';
        return name.includes(normalizedQuery) || barcode.includes(normalizedQuery);
      });
  }, [productsWithMargin, marginFilterDefinition, itemSearch])

  const countPerMarginGroup = useMemo(() => marginFilterOptions.map(f => ({
    value: f.value,
    count: productsWithMargin?.filter(p => f.filterFunc(p.margin,p))?.length ?? 0
  })), [productsWithMargin])


  const toggleDetail = (productId) => {
    if(displayDetails.has(productId)){
      setDisplayDetail((old) =>
        {
          const newSet = new Set(old)
          newSet.delete(productId);
          return newSet;
        })
      }
    else 
    {
      setDisplayDetail((old) =>
        {
          const newSet = new Set(old)
          newSet.add(productId);
          return newSet;
        })
    }
  }

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      if (!products || !platformsCommissions) {
        console.error("No products or platforms data available for export.");
        setIsDownloading(false);
        return;
      }

      const BATCH_SIZE = 20; // Process 100 products at a time
      const allRows = [
       
      ];

      for (let i = 0; i < products.length; i += BATCH_SIZE) {
        const batch = products.slice(i, i + BATCH_SIZE);
        const batchRows = batch.map((p) => ({
          name:p.item.name,
          barcode:p.item.barcode,
          costExclVat:unitFormat(p.costExclVat, user.getCompanyCurrency()),
          vat:p.item.vat,
          ...platformsCommissions.map(platform => {
            const { sellPriceExclVatCommissionExcl, commissionAmount, vatAmount, appliedSellPrice, platformCommissionRate } = computeValues(p, platform,marginType);
            const margin = computeMargin(p, platform,marginType);
            return {
              [`${platform.name}_margin`]:margin?.toFixed(2),
              [`${platform.name}_sellingPrice`]:unitFormat(appliedSellPrice, user.getCompanyCurrency()),
              [`${platform.name}_commissionRate`]:platformCommissionRate?.toFixed(2),
              [`${platform.name}_commissionAmount`]:unitFormat(commissionAmount, user.getCompanyCurrency()),
              [`${platform.name}_vatAmount`]:unitFormat(vatAmount, user.getCompanyCurrency()),
              [`${platform.name}_sellingPriceExclVatExclCommission`]:unitFormat(sellPriceExclVatCommissionExcl, user.getCompanyCurrency())
            };
          }).reduce((acc,curr) => Object.assign(acc,curr),{})
      }));
        allRows.push(...batchRows);

        // Yield to the main thread to prevent freezing
        await new Promise(resolve => setTimeout(resolve, 0)); 
      }

      // Now parse the complete data
      const csv = await parseAsync(allRows,{
        withBOM:true,
        fields:  // Header Row
        [
          {label:'Nom produit',value:'name'},
          {label:'Ean', value:'barcode'},
          {label:'PCE HT',value:'costExclVat'},
          {label:'TVA', value: 'vat'},
          ...platformsCommissions.flatMap(p => [
            {label:"Marge %", value:'margin'},
            {label:"PV TTC", value:'sellingPrice'},
            {label:"Commission %", value:'commissionRate'},
            {label:"Commission HT", value:'commissionAmount'},
            {label:"TVA €", value:'vatAmount'},
            {label:"PV HT hors commission", value:'sellingPriceExclVatExclCommission'},
            
          ].map(a => ({label:`${a.label} ${p.name}`,value: `${p.name}_${a.value}`})))
        ],

      });
      saveFile('margin', csv, 'text/csv')
    } catch (error) {
      console.error("Error generating or downloading CSV:", error);
      // Handle error appropriately, maybe show a message to the user
    } finally {
      setIsDownloading(false);
    }
  }

  const virtuosoRef = useRef(null);

  const platformColumnSizeRaw = (
    16 //max column size
    - 10 // first Product column size
  ) / (platformsCommissions.length + 1);
  const platformColumnSize = Math.floor(platformColumnSizeRaw);
  const firstColumnOffset = platformColumnSize < platformColumnSizeRaw ? 16 - 10 - (platformsCommissions.length + 1)*platformColumnSize : 0;
  return (
    <div>

    <Segment loading={productsLoading} vertical css={css`
    `}>
        
        <Header.Subheader
          css={css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 8px;
            position: relative;
            padding-right: 3rem;
          `}
        >
         

          <Label size='mini' as='a' content={t('margin.labels.noCost','Sans prix achat')} onClick={() => setMarginFilter('NONE')} color='grey' basic={marginFilter !== 'NONE'} detail={countPerMarginGroup.find(f => f.value === 'NONE').count}>
          
          </Label>
          <Label as='a' content={t('margin.labels.negative','Marge < 0%')} onClick={() => setMarginFilter('NEGATIVE')} color='red' basic={marginFilter !== 'NEGATIVE'} detail={countPerMarginGroup.find(f => f.value === 'NEGATIVE').count}>
          
          </Label>
          <Label as='a' content={t('margin.labels.risk','0% < Marge < 30%')} onClick={() => setMarginFilter('RISK')} color='orange' basic={marginFilter !== 'RISK'} detail={countPerMarginGroup.find(f => f.value === 'RISK').count}>
          
          </Label>
          <Label as='a' content={t('margin.labels.fair','Marge > 30% ')} onClick={() => setMarginFilter('FAIR')} color='teal' basic={marginFilter !== 'FAIR'} detail={countPerMarginGroup.find(f => f.value === 'FAIR').count}>
          
          </Label>

           <div
            css={css`
              flex: 1 1 260px;
              min-width: 220px;
              max-width: 360px;
            `}
          >
            <UltyInput
              size='mini'
              value={itemSearch}
              onChange={({ target }) => {setMarginFilter('');setItemSearch(target.value)}}
              placeholder={t('margin.search.placeholder', 'Rechercher un produit')}
            />
          </div>

        {/* <Button onClick={() =>  setMarginType(curr => curr === "ON_SELLING_PRICE" ? "ON_COST" : "ON_SELLING_PRICE")}>
          {marginType === 'ON_SELLING_PRICE' ? "Marge sur prix de vente (Taux de marque)" : "Marge sur prix d'achat (Taux de marge)"}
        </Button> */}
       
        <span css={css`
            position:absolute;
            right:0;
            top:50%;
            transform: translateY(-50%);
          `}>
             {products?.length > 0 && <Button icon onClick={handleDownload} disabled={isDownloading} loading={isDownloading}  >
               <Icon name="cloud download" />
             </Button>}
        </span>
        </Header.Subheader>
      
      <div css={css`
        padding-top: 20px;
        position:relative;
      `}>
      <TableVirtuoso
      data={displayedProduct}
      ref={virtuosoRef}
      overscan={250}
      useWindowScroll
      components={VirtuosoSemanticOverrides}
      defaultItemHeight={57}
      
      fixedHeaderContent={() => (
        <Table.Row>
            <Table.HeaderCell width={10+firstColumnOffset}>{t('margin.details.headers.product', 'Product')}</Table.HeaderCell>
            <Table.HeaderCell width={platformColumnSize}>{t('margin.details.headers.store', 'Magasin')}</Table.HeaderCell>
            {platformsCommissions.map(p => <Table.HeaderCell width={platformColumnSize}>
              {p.name}
            </Table.HeaderCell>)}
          </Table.Row>
      )}
      context={{marginType}}
      itemContent={(index, product,{marginType}) => (
        <>
              <Table.Cell verticalAlign='top' width={10+firstColumnOffset}>
              <Accordion>
                <Accordion.Title css={css`
                  min-height: 57px;
                  width: 100%;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                `}
                  active={displayDetails.has(product.itemId)}
                  onClick={()=> toggleDetail(product.itemId)}
                >
                  <Icon name={displayDetails.has(product.itemId) ? 'minus' : "plus"} size="small" />
                  {product.item.name}
                  <a target="_blank" rel="noreferrer" href={`/products/${product.itemId}`}>&nbsp;<Icon name="external square" size='small'/> </a>
                  <br/>
                  <span style={{fontSize: 'smaller'}}>{product.item.barcode}</span>

                </Accordion.Title>
                <Accordion.Content  active={displayDetails.has(product.itemId)}>
                  {displayDetails.has(product.itemId)  && <DetailProductDisplay product={product} currencyCode={user.getCompanyCurrency()} marginType={marginType}/>}
               
                </Accordion.Content>
                </Accordion>
                </Table.Cell>
              
              <Table.Cell  verticalAlign='top' width={platformColumnSize}>
              <Accordion>
              <Accordion.Title  css={css`
                  min-height: 57px;
                `}
                  active={displayDetails.has(product.itemId)}
                  onClick={()=> toggleDetail(product.itemId)}
                >
                  {product.costExclVat
                    ? (<Label basic color={computeColor(product,computeMarginFromPrice(product,product.sellPrice || product.item.basePrice,null,marginType))}>{computeMarginFromPrice(product,product.sellPrice || product.item.basePrice,null,marginType).toFixed(2)}%</Label>)
                    : (<Label basic color='grey'> Prix d'achat non renseigné</Label>)}
                  </Accordion.Title>
                <Accordion.Content  active={displayDetails.has(product.itemId)}>
                  {displayDetails.has(product.itemId) && <DetailStorePriceDisplay product={product} currencyCode={user.getCompanyCurrency()} marginType={marginType}/>}
                </Accordion.Content>
                </Accordion>

              </Table.Cell>
              {platformsCommissions.map(platform =>  <Table.Cell verticalAlign='top'  width={platformColumnSize} >
                <Accordion>
                <Accordion.Title  css={css`
                  min-height: 57px;
                `}
                  active={displayDetails.has(product.itemId)}
                  onClick={()=> toggleDetail(product.itemId)}
                >
                  {product.costExclVat
                    ? (<Label basic color={computeColor(product,computeMargin(product,platform,marginType))}>{computeMargin(product,platform,marginType).toFixed(2)}%</Label>)
                    : (<Label basic color='grey'> Prix d'achat non renseigné</Label>)}
                  </Accordion.Title>
                <Accordion.Content  active={displayDetails.has(product.itemId)}>
                  {displayDetails.has(product.itemId) &&<DetailPlatformPriceDisplay platform={platform} product={product} currencyCode={user.getCompanyCurrency()}  marginType={marginType}/>}
                </Accordion.Content>
                </Accordion>
                
                </Table.Cell>
              )}
            </>
      )}
      ></TableVirtuoso>
    
      </div>
      </Segment>

      <Segment vertical css={css`
      `}>
    
      </Segment>
      </div>
  )
}

export default MarginProductTable
